// Branding + style tokens for the SAA confirmation email. Brand styling lives here in code (not in
// config): each SAA brand maps to a palette, package name, trust line and logo set. The active brand
// is detected from the loaded config's logo.

import type { SluitstraatConfig } from '@sluitstraat/core';
import { EMAIL_LOGOS } from './saaEmailLogos';
import type { Lang } from './saaEmailCopy';
import { isInformalBrand } from '../i18n/tone';

export interface SaaEmailLogo {
  src: string;
  alt: string;
  width: number;
  height: number;
}

export interface SaaEmailTheme {
  primary: string;
  accent: string;
  pageBg: string;
  cardBg: string;
  headerBg: string;
  line: string;
  textMuted: string;
  footerText: string;
}

export interface SaaEmailBrand {
  packageName: string;
  signoffName: string;
  trustName: string;
  logos: SaaEmailLogo[];
  fallbackText: string;
  theme: SaaEmailTheme;
  informal: boolean;
}

const NEUTRAL = {
  pageBg: '#F4F6F8',
  cardBg: '#FFFFFF',
  headerBg: '#ECEEF1',
  line: '#E3E8EE',
  textMuted: '#51606E',
  footerText: '#6B7280',
};

const SIGNOFF = 'DFD';
const FALLBACK_TEXT = 'SAA Verzekeringen B.V.';

type BrandKey = 'nu91' | 'dfd' | 'spd';

interface LocalizedText {
  nl: string;
  en: string;
}

interface BrandDefinition {
  primary: string;
  accent: string;
  packageName: LocalizedText;
  trustName: LocalizedText;
  logos: Array<{ key: string; width: number }>; // first is left-aligned, last right-aligned
}

const PACKAGE_NAME: LocalizedText = { nl: 'NU-Zekerheid Pakket', en: 'NU-Zekerheid Package' };

const BRANDS: Record<BrandKey, BrandDefinition> = {
  nu91: { primary: '#056666', accent: '#09B4B4', packageName: PACKAGE_NAME, trustName: { nl: "DFD en NU'91", en: "DFD and NU'91" }, logos: [{ key: 'dfd', width: 66 }, { key: 'nu91', width: 150 }] },
  dfd: { primary: '#1E73BE', accent: '#5897FB', packageName: PACKAGE_NAME, trustName: { nl: 'DFD', en: 'DFD' }, logos: [{ key: 'dfd', width: 72 }] },
  spd: { primary: '#0AA8A6', accent: '#009EE2', packageName: PACKAGE_NAME, trustName: { nl: 'DFD en SPD', en: 'DFD and SPD' }, logos: [{ key: 'dfd', width: 66 }, { key: 'spd', width: 150 }] },
};

const detectBrandKey = (config: SluitstraatConfig): BrandKey => {
  const logo = String((config.customer as any)?.logo || '').toLowerCase();
  if (logo.includes('dfd')) return 'dfd';
  if (logo.includes('spd')) return 'spd';
  return 'nu91';
};

export const resolveBrand = (config: SluitstraatConfig, lang: Lang = 'nl'): SaaEmailBrand => {
  const key = detectBrandKey(config);
  const def = BRANDS[key];
  const logos: SaaEmailLogo[] = def.logos.flatMap(({ key, width }) => {
    const asset = EMAIL_LOGOS[key];
    if (!asset) return [];
    return [{ src: asset.src, alt: asset.alt, width, height: Math.round((width * asset.h) / asset.w) }];
  });
  return {
    packageName: def.packageName[lang],
    signoffName: SIGNOFF,
    trustName: def.trustName[lang],
    logos,
    fallbackText: FALLBACK_TEXT,
    theme: { primary: def.primary, accent: def.accent, ...NEUTRAL },
    informal: isInformalBrand(key),
  };
};

// ---- Inline-CSS tokens + helpers (email clients need styles inline) ----

export const FONT = 'Arial, Helvetica, sans-serif';
export const GUTTER = 32; // horizontal padding of content cells
export const RADIUS = 8;
export const ON_DARK = '#FFFFFF';
export const ON_DARK_MUTED = 'rgba(255,255,255,0.8)';
export const ON_DARK_FAINT = 'rgba(255,255,255,0.65)';

export interface TextScale {
  size: number;
  line: number;
}

export const TEXT = {
  h1: { size: 24, line: 30 },
  h2: { size: 18, line: 24 },
  cardTitle: { size: 17, line: 23 },
  lead: { size: 16, line: 24 },
  total: { size: 16, line: 22 },
  body: { size: 15, line: 24 },
  meta: { size: 14, line: 22 },
  base: { size: 14, line: 21 },
  row: { size: 14, line: 20 },
  small: { size: 13, line: 20 },
  fine: { size: 11, line: 17 },
} as const;

export const css = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join('; ');

export const font = (scale: TextScale, color: string, weight?: 'bold'): string =>
  css(`font-family:${FONT}`, `font-size:${scale.size}px`, `line-height:${scale.line}px`, `color:${color}`, weight && 'font-weight:bold');

export const contentCell = (top: number, bottom = 0): string =>
  css(`padding:${top}px ${GUTTER}px ${bottom}px ${GUTTER}px`, `font-family:${FONT}`);

export const escapeHtml = (value: string): string =>
  String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
